import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, Lock, Zap, IndianRupee, Receipt } from "lucide-react";
import { getRun, listPayouts, RunSummary, Payout } from "../../../api/settlement";
import { useNotice } from "../shared/useNotice";

// A print-ready settlement statement. Standalone (no maintenance chrome) so it
// prints clean to A4 / PDF. All figures come from the run the engine computed.

const inr = (n: number) =>
  `₹${(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n: number) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
const fmtDate = (d: string | null) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtPeriodKey = (key: string) => {
  if (key.includes("-Q")) { const [y, q] = key.split("-Q"); return `Q${q} ${y}`; }
  const [y, m] = key.split("-"); const mi = Number(m) - 1;
  return mi >= 0 && mi < 12 ? `${MONTHS[mi]} ${y}` : key;
};
const GST_LABEL: Record<string, string> = { exempt: "Exempt", gst_18_forward: "18% fwd", gst_18_rcm: "18% RCM" };

const C = {
  ink: "#0f172a", sub: "#64748b", line: "#e2e8f0", soft: "#f8fafc",
  teal: "#0d9488", tealDark: "#0f766e", green: "#16a34a", greenBg: "#ecfdf5",
  amber: "#b45309", amberBg: "#fffbeb",
};

const th: React.CSSProperties = {
  textAlign: "left", padding: "9px 10px", fontSize: 11, fontWeight: 700, color: C.sub,
  textTransform: "uppercase", letterSpacing: ".04em", borderBottom: `2px solid ${C.line}`,
};
const thR: React.CSSProperties = { ...th, textAlign: "right" };
const td: React.CSSProperties = { padding: "9px 10px", fontSize: 13, color: C.ink, borderBottom: `1px solid ${C.line}` };
const tdR: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

const SettlementReport: React.FC = () => {
  const { orgId: orgIdRaw, periodKey } = useParams();
  const orgId = Number(orgIdRaw);
  const navigate = useNavigate();

  const [run, setRun] = useState<RunSummary | null>(null);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useNotice("error", "");

  useEffect(() => {
    if (!Number.isFinite(orgId) || !periodKey) { setError("Invalid report link"); setLoading(false); return; }
    let active = true;
    setLoading(true);
    Promise.all([getRun(orgId, periodKey), listPayouts(orgId, periodKey).catch(() => [])])
      .then(([r, p]) => { if (active) { setRun(r); setPayouts(p); } })
      .catch((err) => { if (active) setError(err?.response?.data?.detail || "Failed to load the settlement"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [orgId, periodKey]);

  if (loading) return <div style={{ padding: 40, color: C.sub, fontFamily: "system-ui" }}>Loading statement…</div>;
  if (error || !run)
    return (
      <div style={{ padding: 40, fontFamily: "system-ui" }}>
        <p style={{ color: "#c0392b" }}>{error || "Not found"}</p>
        <button onClick={() => navigate(-1)} style={{ ...toolBtn }}>Back</button>
      </div>
    );

  const settled = run.status === "settled";
  const totReimb = run.parties.reduce((s, p) => s + p.reimbursement, 0);
  const totShare = run.parties.reduce((s, p) => s + p.share, 0);
  const totGst = run.parties.reduce((s, p) => s + p.gst, 0);
  const totPayable = run.parties.reduce((s, p) => s + p.total_payable, 0);
  const totPaid = run.parties.reduce((s, p) => s + p.paid, 0);
  const totKwh = run.profiles.reduce((s, p) => s + p.kwh, 0);
  const balance = Math.max(0, totPayable - totPaid);

  return (
    <div style={{ background: "#eef2f6", minHeight: "100vh", padding: "24px 12px", fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif" }}>
      <style>{`
        @media print {
          .report-toolbar { display: none !important; }
          body, #root { background: #fff !important; }
          .report-page { background: #fff !important; padding: 0 !important; }
          .report-doc { box-shadow: none !important; margin: 0 !important; max-width: none !important; }
          @page { size: A4; margin: 13mm; }
          .tier-card, tr, .kpi { break-inside: avoid; }
        }
      `}</style>

      <div className="report-page" style={{ maxWidth: 880, margin: "0 auto" }}>
        {/* toolbar (screen only) */}
        <div className="report-toolbar" style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          <button onClick={() => navigate(-1)} style={toolBtn}><ArrowLeft size={15} /> Back</button>
          <button onClick={() => window.print()} style={{ ...toolBtn, background: C.teal, color: "#fff", border: "none" }}>
            <Printer size={15} /> Print / Save PDF
          </button>
        </div>

        {/* document */}
        <div className="report-doc" style={{ background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 8px 30px rgba(15,23,42,.10)" }}>
          {/* header band */}
          <div style={{ background: `linear-gradient(135deg, ${C.tealDark}, ${C.teal})`, color: "#fff", padding: "22px 28px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 18, letterSpacing: ".2px" }}>
                  <span style={{ background: "rgba(255,255,255,.18)", borderRadius: 8, padding: "4px 8px", display: "inline-flex" }}><Zap size={18} /></span>
                  ChargingAdda
                </div>
                <div style={{ opacity: .85, fontSize: 12.5, marginTop: 6 }}>Settlement Statement</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: ".5px" }}>{fmtPeriodKey(run.period_key)}</div>
                <div style={{ opacity: .85, fontSize: 12.5 }}>{fmtDate(run.period_start)} &ndash; {fmtDate(run.period_end)}</div>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8, padding: "3px 10px", borderRadius: 999,
                  background: settled ? "rgba(255,255,255,.95)" : "rgba(255,255,255,.18)", color: settled ? C.tealDark : "#fff",
                  fontSize: 12, fontWeight: 700,
                }}>
                  {settled ? <><Lock size={12} /> Settled</> : "Draft"}
                </span>
              </div>
            </div>
          </div>

          {/* meta */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 1, background: C.line }}>
            {[
              ["Settled for", run.org_name || `Org #${run.org_id}`],
              ["Status", settled ? `Settled on ${fmtDate(run.settled_at)}` : "Draft (not yet locked)"],
              ["Prepared by", run.computed_by || "—"],
              ["Authorised by", settled ? (run.settled_by || "—") : "—"],
            ].map(([k, v]) => (
              <div key={k} style={{ background: "#fff", padding: "14px 28px" }}>
                <div style={{ fontSize: 11, color: C.sub, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700 }}>{k}</div>
                <div style={{ fontSize: 14, color: C.ink, marginTop: 3, fontWeight: 600 }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ padding: "22px 28px 28px" }}>
            {/* KPI strip */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 22 }}>
              <Kpi icon={<IndianRupee size={15} />} label="Total payable" value={inr(totPayable)} accent={C.teal} />
              <Kpi icon={<Zap size={15} />} label="Energy settled" value={`${num(totKwh)} kWh`} accent={C.green} />
              <Kpi icon={<Receipt size={15} />} label="Output GST" value={inr(run.gst_ledger.output_gst)} accent={C.amber} />
              <Kpi icon={<IndianRupee size={15} />} label="Balance to pay" value={inr(balance)} accent={balance > 0 ? "#dc2626" : C.green} />
            </div>

            {/* party payouts */}
            <Section title="Payouts by party">
              <table style={tbl}>
                <thead><tr>
                  <th style={th}>Party</th><th style={thR}>Consumables</th><th style={thR}>Share</th>
                  <th style={thR}>GST</th><th style={thR}>Total</th><th style={thR}>Paid</th><th style={thR}>Balance</th>
                </tr></thead>
                <tbody>
                  {run.parties.map((p) => (
                    <tr key={p.party_id}>
                      <td style={td}><b>{p.party_name}</b> <span style={{ color: C.sub, fontSize: 11.5 }}>({p.party_type})</span></td>
                      <td style={tdR}>{inr(p.reimbursement)}</td>
                      <td style={tdR}>{inr(p.share)}</td>
                      <td style={tdR}>{inr(p.gst)}</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{inr(p.total_payable)}</td>
                      <td style={tdR}>{inr(p.paid)}</td>
                      <td style={{ ...tdR, color: p.total_payable - p.paid > 0.01 ? "#dc2626" : C.green, fontWeight: 600 }}>
                        {inr(Math.max(0, p.total_payable - p.paid))}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ ...td, fontWeight: 800, borderTop: `2px solid ${C.line}` }}>Total</td>
                    <td style={{ ...tdR, fontWeight: 800, borderTop: `2px solid ${C.line}` }}>{inr(totReimb)}</td>
                    <td style={{ ...tdR, fontWeight: 800, borderTop: `2px solid ${C.line}` }}>{inr(totShare)}</td>
                    <td style={{ ...tdR, fontWeight: 800, borderTop: `2px solid ${C.line}` }}>{inr(totGst)}</td>
                    <td style={{ ...tdR, fontWeight: 800, borderTop: `2px solid ${C.line}` }}>{inr(totPayable)}</td>
                    <td style={{ ...tdR, fontWeight: 800, borderTop: `2px solid ${C.line}` }}>{inr(totPaid)}</td>
                    <td style={{ ...tdR, fontWeight: 800, borderTop: `2px solid ${C.line}` }}>{inr(balance)}</td>
                  </tr>
                </tfoot>
              </table>
            </Section>

            {/* GST ledger */}
            <Section title="GST ledger">
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14, fontSize: 14, color: C.ink, padding: "4px 2px" }}>
                <span>Output GST collected <b>{inr(run.gst_ledger.output_gst)}</b></span>
                <span style={{ color: C.sub }}>&minus;</span>
                <span>Input credit (ITC) <b>{inr(run.gst_ledger.input_credit)}</b></span>
                <span style={{ color: C.sub }}>=</span>
                <span style={{ background: C.greenBg, color: C.tealDark, padding: "5px 14px", borderRadius: 999, fontWeight: 800 }}>
                  Net GST payable {inr(run.gst_ledger.net_payable)}
                </span>
              </div>
            </Section>

            {/* per-tier breakdown */}
            {run.profiles.length > 0 && (
              <Section title="How each price tier was split">
                {run.profiles.map((pr, i) => (
                  <div key={pr.profile_id ?? i} className="tier-card" style={{ border: `1px solid ${C.line}`, borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, padding: "10px 12px", background: C.soft, borderBottom: `1px solid ${C.line}` }}>
                      <b style={{ color: C.ink }}>{pr.station_name || "—"}</b>
                      <span style={chip}>{pr.charger_type || "AC"}</span>
                      {pr.price_org_name && <span style={{ ...chip, background: "#eef2ff", color: "#3730a3" }}>{pr.price_org_name}</span>}
                      <span style={{ marginLeft: "auto", fontSize: 12.5, color: C.sub }}>
                        {num(pr.kwh)} kWh &middot; taxable <b style={{ color: C.ink }}>₹{(pr.taxable_unit_price ?? 0).toFixed(2)}/kWh</b>
                      </span>
                    </div>
                    <table style={tbl}>
                      <thead><tr>
                        <th style={th}>Line</th><th style={th}>Party</th><th style={thR}>₹/kWh</th>
                        <th style={thR}>Amount</th><th style={th}>GST</th><th style={thR}>GST ₹</th>
                      </tr></thead>
                      <tbody>
                        {pr.lines.map((l, j) => (
                          <tr key={j}>
                            <td style={td}>
                              <span style={{ ...chip, background: l.line_kind === "consumable" ? C.amberBg : C.greenBg, color: l.line_kind === "consumable" ? C.amber : C.tealDark }}>
                                {l.line_kind === "consumable" ? "Consumable" : "Share"}
                              </span>{" "}
                              <span style={{ color: C.ink }}>{l.label}</span>
                            </td>
                            <td style={td}>{l.party_name || "—"}</td>
                            <td style={tdR}>{l.rate_per_kwh.toFixed(2)}</td>
                            <td style={{ ...tdR, fontWeight: 600 }}>{inr(l.amount)}</td>
                            <td style={td}>{GST_LABEL[l.gst_treatment] || l.gst_treatment}</td>
                            <td style={tdR}>{l.gst_amount ? inr(l.gst_amount) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </Section>
            )}

            {/* uncovered */}
            {run.uncovered.kwh > 0 && (
              <div style={{ background: C.amberBg, border: `1px solid #fde68a`, color: C.amber, borderRadius: 10, padding: "12px 14px", fontSize: 13, marginBottom: 18 }}>
                <b>Not attributed:</b> {num(run.uncovered.kwh)} kWh across {run.uncovered.sessions.toLocaleString("en-IN")} sessions at stations without a settlement profile (device-rate / legacy). Excluded from the payouts above.
              </div>
            )}

            {/* payout history */}
            {payouts.length > 0 && (
              <Section title="Payments recorded">
                <table style={tbl}>
                  <thead><tr><th style={th}>Party</th><th style={thR}>Amount</th><th style={th}>Reference</th><th style={th}>Date</th></tr></thead>
                  <tbody>
                    {payouts.map((p) => (
                      <tr key={p.id}>
                        <td style={td}>{p.party_name}</td><td style={tdR}>{inr(p.amount)}</td>
                        <td style={td}>{p.reference || "—"}</td><td style={td}>{fmtDate(p.paid_on)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}

            {/* footer */}
            <div style={{ marginTop: 24, paddingTop: 14, borderTop: `1px solid ${C.line}`, fontSize: 11.5, color: C.sub, lineHeight: 1.6 }}>
              {settled
                ? "This is a settled statement — the figures are frozen and auditable. Payments are recorded separately and do not change the figures above."
                : "Draft — figures may change until the quarter is settled."}{" "}
              Energy delivered &times; the agreed per-kWh rate card. GST is an overlay on the taxable base. Generated by ChargingAdda Settlements.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const toolBtn: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", color: C.ink,
  border: `1px solid ${C.line}`, borderRadius: 9, padding: "8px 14px", fontWeight: 600, cursor: "pointer", fontSize: 13.5,
};
const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const chip: React.CSSProperties = { display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: "#eef2f6", color: "#475569" };

const Kpi: React.FC<{ icon: React.ReactNode; label: string; value: string; accent: string }> = ({ icon, label, value, accent }) => (
  <div className="kpi" style={{ border: `1px solid ${C.line}`, borderRadius: 12, padding: "13px 15px", borderTop: `3px solid ${accent}` }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, color: C.sub, fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".03em" }}>
      <span style={{ color: accent }}>{icon}</span>{label}
    </div>
    <div style={{ fontSize: 21, fontWeight: 800, color: C.ink, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>{value}</div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 22 }}>
    <h3 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 800, color: C.ink, textTransform: "uppercase", letterSpacing: ".04em" }}>{title}</h3>
    {children}
  </div>
);

export default SettlementReport;
